/**
 * Version timeline list — shows all invoice versions with click-to-view.
 */

import { Modal, Button, Badge } from '@elsapiens/ui'
import { History, Clock, User, ChevronRight } from 'lucide-react'

import type { InvoiceVersion } from '@/types/billing'
import { formatCurrency } from '@/utils/formatters'

import { formatShortDate } from './invoice-version-types'

import type { RawInvoiceSnapshot } from './invoice-version-types'

interface VersionTimelineProps {
  isOpen: boolean
  isLoading: boolean
  versions: InvoiceVersion[]
  baseCurrency: string
  onViewVersion: (versionNumber: number) => void
  onClose: () => void
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="animate-pulse flex gap-3 items-start">
          <div className="w-8 h-8 rounded-full bg-surface-secondary shrink-0" />
          <div className="flex-1">
            <div className="h-4 bg-surface-secondary rounded w-1/3 mb-2" />
            <div className="h-3 bg-surface-secondary rounded w-2/3" />
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-surface-secondary flex items-center justify-center mb-3">
        <History className="w-6 h-6 text-foreground-secondary" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">No version history</p>
      <p className="text-xs text-foreground-secondary max-w-xs">
        Versions are saved automatically when a posted invoice is edited.
      </p>
    </div>
  )
}

function VersionRow({ version, index, baseCurrency, onView }: {
  version: InvoiceVersion
  index: number
  baseCurrency: string
  onView: () => void
}): JSX.Element {
  const rawSnap = version.invoiceSnapshot as unknown as RawInvoiceSnapshot
  const cur = (amount: number, currency?: string): string =>
    formatCurrency(amount || 0, currency ?? baseCurrency)

  return (
    <button
      className="relative flex items-start gap-3 el-p-sm rounded-lg text-left transition-colors hover:bg-surface-secondary group"
      type="button"
      onClick={onView}
    >
      <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
        index === 0
          ? 'bg-accent text-primary ring-2 ring-ring'
          : 'bg-surface-secondary text-foreground-secondary'
      }`}>
        {version.versionNumber}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium text-foreground">
            Version {version.versionNumber}
          </span>
          {index === 0 && <Badge size="sm" variant="info">Latest</Badge>}
          <span className="ml-auto text-sm font-mono font-medium text-foreground">
            {cur(Number(rawSnap.total_amount) || 0, rawSnap.currency)}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-secondary">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatShortDate(version.createdAt)}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {version.createdBy}
          </span>
        </div>
        {version.changeNotes && (
          <p className="text-xs text-foreground-secondary mt-1 truncate">{version.changeNotes}</p>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-foreground-secondary opacity-0 group-hover:opacity-100 transition-opacity mt-1.5 shrink-0" />
    </button>
  )
}

export function VersionTimeline({
  isOpen, isLoading, versions, baseCurrency, onViewVersion, onClose,
}: VersionTimelineProps): JSX.Element {
  return (
    <Modal
      description="Previous snapshots of this invoice"
      footer={
        <div className="flex justify-end w-full">
          <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
        </div>
      }
      isOpen={isOpen}
      size="md"
      title="Version History"
      onClose={onClose}
    >
      <div className="min-h-[200px]">
        {isLoading && <LoadingSkeleton />}
        {!isLoading && versions.length === 0 && <EmptyState />}
        {!isLoading && versions.length > 0 && (
          <div className="relative">
            <div className="absolute left-4 top-6 bottom-6 w-px bg-border" />
            <div className="flex flex-col gap-1">
              {versions.map((version, index) => (
                <VersionRow
                  key={version.id}
                  baseCurrency={baseCurrency}
                  index={index}
                  version={version}
                  onView={() => { onViewVersion(version.versionNumber) }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
